import {ReactElement, useEffect, useMemo, useRef, useState} from "react";
import * as css from "./unitTypeSyncWithPMSTable.module.scss";
import {Button} from "@zendeskgarden/react-buttons";
import {Inline} from "@zendeskgarden/react-loaders";
import {COLORS} from "../../../../constants/Colors";
import HotTable, {HotColumn} from "@handsontable/react";
import {LICENSES} from "../../../../constants/Licenses";
import Handsontable from "handsontable";
import cn from "classnames";

import {CellValue} from "handsontable/common";
import {buildHOTData, HOTData, mergeUpdates, PendingUpdates, TUnitTypeData} from "./logic";
import {CellRenderer} from "./CellRenderer";
import { useProperties } from "../../../../contexts/properties/PropertiesContext";

interface UnitTypeSyncWithPMSTableProps {
    data: TUnitTypeData[] | undefined,
    valuesStartFromIndex: number,
    isApplying: boolean,
    onCancel: () => void,
    onApply: (updates: {
        unitTypeId: string;
        values: (number | null | undefined)[];
    }[]) => void;
}

export function UnitTypeSyncWithPMSTable(props: UnitTypeSyncWithPMSTableProps): ReactElement {
    const hotRef = useRef<HotTable>(null);

    const [hotData, setHotData] = useState<HOTData>();
    const [hiddenRows, setHiddenRows] = useState<number[]>([]);
    const [pendingUpdates, setPendingUpdates] = useState<PendingUpdates>();
    const {currentProperty: property} = useProperties();

    function beforeChange(changes: Array<Handsontable.CellChange | null>, source: Handsontable.ChangeSource) {
        if(!changes || source == "loadData" || !hotRef.current || !hotRef.current.hotInstance || !hotData) {
            return;
        }
        const updates: PendingUpdates = {unitTypeUpdates: new Map()};
        for(const change of changes) {
            if(change === null) {
                continue;
            }
            const [row, column, oldValue, newValue] = change;
            const col = typeof column === "number" ? column : parseInt(column);
            if(col > 0 && col - 1 < 12) {
                const unitTypeId = hotData.rowNumToUnitTypeId[row];
                if(!unitTypeId) {
                    continue;
                }
                let unitTypeUpdate = updates.unitTypeUpdates.get(unitTypeId);
                if(!unitTypeUpdate) {
                    unitTypeUpdate = new Array<number | null | undefined>(12).fill(undefined);
                    updates.unitTypeUpdates.set(unitTypeId, unitTypeUpdate);
                }
                let setValue: number | null = null;
                if(newValue !== null) {
                    if(typeof newValue == "number") {
                        setValue = newValue;
                    }
                    else if (typeof newValue == "string" && !isNaN(+newValue)) {
                        setValue = +newValue;
                    }
                }
                if(setValue == null) {
                    change[3] = 0;
                    unitTypeUpdate[col - 1] = 0;
                }
                else {
                    unitTypeUpdate[col - 1] = setValue;
                }
            }
        }
        const merged = mergeUpdates(pendingUpdates, updates, hotData.currentValuesByUnitTypeId);
        setPendingUpdates(merged);
    }

    function handleRowToggleClick(row: number) {
        if(!hotData) {
            return;
        }
        const unitTypeId = hotData.rowNumToUnitTypeId[row];
        if(!unitTypeId) {
            return;
        }
        if(hiddenRows.includes(row + 1)) {
            setHiddenRows(hiddenRows.filter(r => r !== row + 1 && r !== row + 2));
        }
        else {
            setHiddenRows([...hiddenRows, row + 1, row + 2]);
        }
    }

    function handleAllRowsToggleClick() {
        if(!hotData) {
            return;
        }
        if(hiddenRows.length == 0) {
            const hiddenRows = Object.keys(hotData.rowNumToUnitTypeId).flatMap(row => [+row + 1, +row + 2]);
            setHiddenRows(hiddenRows);
        }
        else {
            setHiddenRows([]);
        }
    }

    function handleResetToCurrent() {
        if(!props.data || !hotData) {
            return;
        }
        const changes: Array<[number, number, CellValue]> = [];
        const idToRow: Record<string, number> = {};
        for(const [row, id] of Object.entries(hotData.rowNumToUnitTypeId)) {
            idToRow[id] = +row;
        }
        for(const utData of props.data) {
            const row = idToRow[utData.unitTypeId];
            if(row === undefined) {
                continue;
            }
            for(let i = 0; i < 12; i++) {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                changes.push([row, i + 1, utData.currentForecastedValues[i]!]);
            }
        }
        hotRef.current?.hotInstance?.setDataAtCell(changes, "loadData");
        setPendingUpdates(undefined);
    }

    function handleApplyPMS() {
        if(!props.data || !hotData) {
            return;
        }
        const changes: Array<[number, number, CellValue]> = [];
        const idToRow: Record<string, number> = {};
        const updates: PendingUpdates = {unitTypeUpdates: new Map()};
        for(const [row, id] of Object.entries(hotData.rowNumToUnitTypeId)) {
            idToRow[id] = +row;
        }
        for(const utData of props.data) {
            const row = idToRow[utData.unitTypeId];
            if(row === undefined) {
                continue;
            }
            const utUpdates = new Array<number | null | undefined>(12).fill(undefined);
            updates.unitTypeUpdates.set(utData.unitTypeId, utUpdates);
            for(let i = 0; i < 12; i++) {
                const val = utData.pmsValues[i];
                if(val !== null) {
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    changes.push([row, i + 1, val]);
                    utUpdates[i] = val;
                }
            }
        }
        hotRef.current?.hotInstance?.setDataAtCell(changes, "loadData");
        const merged = mergeUpdates(pendingUpdates, updates, hotData.currentValuesByUnitTypeId);
        setPendingUpdates(merged);
    }

    useEffect(() => {
        if(props.data) {
            const {data: hotData, updates} = buildHOTData(props.data);
            const hiddenRows = Object.keys(hotData.rowNumToUnitTypeId).flatMap(row => [+row + 1, +row + 2]);
            setHotData(hotData);
            setPendingUpdates(updates);
            setHiddenRows(hiddenRows);

        }
        else {
            setHotData(undefined);
            setPendingUpdates(undefined);
        }
    }, [props.data]);

    const canApply = useMemo(() => {
        let canApply = !props.isApplying && pendingUpdates != undefined;
        if(canApply && pendingUpdates) {
            canApply = Array.from(pendingUpdates.unitTypeUpdates.values())
                .filter(row => row.some(v => v !== undefined)).length > 0;
        }
        return canApply;
    }, [pendingUpdates, props.isApplying]);

    function handleCancel() {
        props.onCancel();
    }

    function handleApply() {
        if(!pendingUpdates) {
            return;
        }
        const casted = Array.from(pendingUpdates.unitTypeUpdates)
            .map(([unitTypeId, values]) => ({unitTypeId, values}));
        props.onApply(Array.from(casted));
    }

    return (
        <>
            <div className={css.legend}>
                <div className={css.legendBox}>99.9</div>
                <span className={css.legendText}>CHANGED</span>
                <div className={cn(css.legendBox, css.noChange)}>94.5</div>
                <span className={css.legendText}>NO CHANGE</span>
            </div>
            <div className={css.tableWrapper}>
                <HotTable
                    ref={hotRef}
                    data={hotData?.tableData}
                    colWidths={[200,
                        86.4, 86.4, 86.4, 86.4, 86.4, 86.4, 86.4, 86.4, 86.4, 86.4, 86.4, 86.4
                    ]}
                    rowHeights={50}
                    readOnly={false}
                    settings={{
                        className: "htMiddle",
                        manualRowResize: false,
                        manualColumnResize: false,
                        autoColumnSize: false,
                        autoRowSize: false,
                        stretchH: "none",
                        hiddenRows: {
                            rows: hiddenRows,
                            //@ts-expect-error: For some reason our version of handsontable does not provide correct API shape but does support copyPasteEnabled
                            copyPasteEnabled: false
                        },
                        fixedColumnsLeft: 1,
                        fixedRowsTop: 1,
                    }}
                    beforeChange={beforeChange}
                    licenseKey={LICENSES.HandsOnTable}
                    style={{margin: "auto"}}
                >
                    {hotData?.colHeaders?.map((col) =>
                        <HotColumn key={col}>
                            <CellRenderer hot-renderer
                                valuesStartFromIndex={props.valuesStartFromIndex}
                                rowNumToUnitTypeId={hotData.rowNumToUnitTypeId}
                                pendingUpdates={pendingUpdates}
                                handleRowToggleClick={handleRowToggleClick}
                                handleAllRowsToggleClick={handleAllRowsToggleClick}
                                isEditing={true}
                                allRowsExpanded={hiddenRows.length == 0}
                                hiddenRows={hiddenRows}
                            />
                        </HotColumn>
                    )}
                </HotTable>
            </div>
            <div className={css.footerWrapper}>
                <div className={css.buttonsLeft}>
                    <Button onClick={handleResetToCurrent}>Use Current Values</Button>
                    { property && !property.isAcquisitionProperty &&
                        <Button onClick={handleApplyPMS}>Sync with PMS</Button>
                    }
                </div>
                <div className={css.buttonsRight}>
                    <Button onClick={() => handleCancel()} isBasic>Cancel</Button>
                    <Button
                        onClick={() => handleApply()}
                        isPrimary
                        disabled={!canApply}
                    >
                        {props.isApplying
                            ? <Inline size={24} color={COLORS.POSITIVE_COLOR} aria-label="loading" />
                            :
                            "Save"
                        }
                    </Button>
                </div>
            </div>
        </>
    );
}

